/**
 * API Service for Saved Jobs
 * Connects to Job-Service backend for save/unsave functionality
 * Uses candidateId from authUser in localStorage
 */

import axios from 'axios';
import { Job } from '../../recruiter/services/jobApi';

const JOB_SERVICE_URL = import.meta.env.VITE_JOB_SERVICE_URL || 'http://localhost:8003';

/**
 * Get the current user's Supabase ID from localStorage
 */
const getCandidateId = (): string => {
    const userDataString = localStorage.getItem('authUser');
    if (userDataString) {
        try {
            const userData = JSON.parse(userDataString);
            return userData.id || '';
        } catch {
            return '';
        }
    }
    return '';
};

/**
 * Get the auth token from localStorage
 */
const getAuthToken = (): string => {
    return localStorage.getItem('authToken') || '';
};

/**
 * Create axios headers with auth token
 */
const getAuthHeaders = () => ({
    Authorization: `Bearer ${getAuthToken()}`
});

export interface SavedJobsResponse {
    payload: {
        data: Job[];
        pagination: {
            page: number;
            size: number;
            total: number;
            totalPages: number;
        };
    };
    status: {
        msg: string;
        error: boolean;
    };
}

export const savedJobsApi = {
    /**
     * Get all saved jobs for current user
     * @param page - Page number (default 1)
     * @param size - Page size (default 100)
     */
    getSavedJobs: async (page = 1, size = 100): Promise<SavedJobsResponse> => {
        const candidateId = getCandidateId();
        if (!candidateId) {
            throw new Error('User not authenticated');
        }

        const response = await axios.get(
            `${JOB_SERVICE_URL}/jobs/v1/saved?candidateId=${candidateId}&page=${page}&size=${size}`,
            { headers: getAuthHeaders() }
        );

        return response.data;
    },

    /**
     * Save a job for current user
     * @param jobId - The job ID to save
     */
    saveJob: async (jobId: string): Promise<void> => {
        const candidateId = getCandidateId();
        if (!candidateId) {
            throw new Error('User not authenticated');
        }

        await axios.post(
            `${JOB_SERVICE_URL}/jobs/v1/${jobId}/save?candidateId=${candidateId}`,
            {},
            { headers: getAuthHeaders() }
        );
    },

    /**
     * Unsave a job for current user
     * @param jobId - The job ID to unsave
     */
    unsaveJob: async (jobId: string): Promise<void> => {
        const candidateId = getCandidateId();
        if (!candidateId) {
            throw new Error('User not authenticated');
        }

        await axios.delete(
            `${JOB_SERVICE_URL}/jobs/v1/${jobId}/save?candidateId=${candidateId}`,
            { headers: getAuthHeaders() }
        );
    },

    /**
     * Check if a job is saved by current user
     * @param jobId - The job ID to check
     * @param savedJobIds - Set of saved job IDs
     */
    isSaved: (jobId: string, savedJobIds: Set<string>): boolean => {
        return savedJobIds.has(jobId);
    }
};
